import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Avatar,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Grid,
  Chip,
  Button,
  Link,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ref, get, onValue, off, set } from 'firebase/database';
import { database } from '../../firebase';
import { useUser } from '../../contexts/UserContext';
import { useAuth } from '../../hooks/useAuth';
import { useSchools } from '../../contexts/SchoolsContext';
import { Student } from '../../types/Student';
import { userToStudentAdapter } from '../../utils/userToStudentAdapter';
import EligibilityIcon, { EligibilityData } from '../shared/EligibilityIcon';
import { activityLogService } from '../../services/activityLogService';
import CustomEligibilityIndicatorsModal from '../CustomEligibilityIndicatorsModal';

// Helper to ensure a website URL starts with http(s)://
function formatWebsiteUrl(url?: string): string {
  if (!url) return '';
  
  // If the URL already has a protocol, return as is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // Otherwise, add https://
  return `https://${url}`;
}

const StudentProfileForGuidance: React.FC = () => {
  const { studentId } = useParams<{ studentId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  // Detect mobile landscape or smaller tablets (typically 667px - 1024px width)
  // This covers mobile landscape and small tablets where we need to wrap course names
  const isMobileLandscape = useMediaQuery('(max-width: 1024px) and (min-width: 667px)');
  const { user: currentUser } = useUser();
  const { user: authUser } = useAuth();
  const { schools } = useSchools();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copyingCourses, setCopyingCourses] = useState(false);
  const [studentData, setStudentData] = useState<any>(null);
  const [selectedTab, setSelectedTab] = useState(0);
  
  // Custom Eligibility Indicators modal state
  const [eligibilityIndicatorsModalOpen, setEligibilityIndicatorsModalOpen] = useState(false);
  const [courseOptions, setCourseOptions] = useState<{ code: string; description: string }[]>([]);

  // Fetch course options from Firebase to get course names (must be called before any early returns)
  useEffect(() => {
    const fetchCourseOptions = async () => {
      try {
        const coursesRef = ref(database, 'highSchoolCourses_new');
        const snapshot = await get(coursesRef);
        
        if (snapshot.exists()) {
          const coursesData = snapshot.val();
          const allCourses: { code: string; description: string }[] = [];
          
          for (const courseCode in coursesData) {
            const courseData = coursesData[courseCode] as any;
            const name = courseData.Name || '';
            if (name) {
              allCourses.push({
                code: courseCode,
                description: name
              });
            }
          }
          
          setCourseOptions(allCourses);
        }
      } catch (error) {
        console.error('Error fetching course options:', error);
      }
    };
    
    fetchCourseOptions();
  }, []);

  // Handle copying courses from student to guidance user
  const handleCopyCoursesToGuidanceUser = async () => {
    if (!authUser?.uid || !studentId) {
      return;
    }

    setCopyingCourses(true);
    try {
      // Get the student's grades (already loaded in studentData.grades)
      const studentGrades = studentData?.grades || {};
      
      if (!studentGrades || Object.keys(studentGrades).length === 0) {
        alert('This student has no courses and grades entered.');
        setCopyingCourses(false);
        return;
      }

      // Save backup of current guidance user's grades before copying
      const guidanceUserGradesRef = ref(database, `user/${authUser.uid}/grades`);
      const currentGradesSnapshot = await get(guidanceUserGradesRef);
      const originalGrades = currentGradesSnapshot.exists() ? currentGradesSnapshot.val() : {};
      
      // Save original grades as backup in a temporary location
      const backupRef = ref(database, `user/${authUser.uid}/grades_backup`);
      await set(backupRef, originalGrades);

      // Clear guidance user's existing grades and replace with student's grades
      await set(guidanceUserGradesRef, studentGrades);

      // Navigate to Profile page with Courses & Grades tab and edit mode
      // Use location state to indicate we should enter edit mode and that we have a backup
      navigate('/my-student-profile', { 
        state: { 
          selectedTab: 0, // Academics tab (index 0)
          editGrades: true, // Flag to trigger edit mode
          hasBackup: true // Flag to indicate we should restore backup on cancel
        } 
      });
    } catch (error) {
      console.error('Error copying courses to guidance user:', error);
      alert('Failed to copy courses and grades. Please try again.');
    } finally {
      setCopyingCourses(false);
    }
  };
  const [favoriteSchools, setFavoriteSchools] = useState<string[]>([]);
  const [favoriteSchoolsDetailed, setFavoriteSchoolsDetailed] = useState<Array<{
    sortOrder: number;
    schoolName: string;
    schoolId?: string;
  }>>([]);
  const [favoritePrograms, setFavoritePrograms] = useState<string[]>([]);
  const [favoriteProgramCategories, setFavoriteProgramCategories] = useState<string[]>([]);
  const [favoriteProgramsDetailed, setFavoriteProgramsDetailed] = useState<Array<{
    sortOrder: number;
    programName: string;
    schoolName: string;
    programId: string;
    schoolId?: string;
  }>>([]);
  const [programsData, setProgramsData] = useState<any>({});
  const [schoolsData, setSchoolsData] = useState<any>({});
  const [eligibilityCache, setEligibilityCache] = useState<Record<string, any>>({});

  useEffect(() => {
    const fetchStudentData = async () => {
      if (!studentId) {
        setError('Student ID is required');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Fetch user data (ProfileData, etc.)
        const userRef = ref(database, `user/${studentId}`);
        const snapshot = await get(userRef);

        if (!snapshot.exists()) {
          setError('Student not found');
          setLoading(false);
          return;
        }

        const userData = snapshot.val();
        
        // Try to fetch grades separately in case they're not included in main user fetch
        let gradesData = userData.grades || {};
        try {
          const gradesRef = ref(database, `user/${studentId}/grades`);
          const gradesSnapshot = await get(gradesRef);
          if (gradesSnapshot.exists()) {
            gradesData = gradesSnapshot.val();
          }
        } catch (gradesErr: any) {
          // Silently use grades from user data if separate fetch fails
        }
        
        // Merge grades into userData
        userData.grades = gradesData;
        
        setStudentData(userData);

        // Verify the student belongs to the guidance user's school
        const profileData = userData.ProfileData || {};
        const studentSchoolID = profileData.hsID || '';

        if (currentUser?.schoolID && currentUser.schoolID !== studentSchoolID) {
          setError('You do not have permission to view this student');
          setLoading(false);
          return;
        }

        // Fetch favorites data
        try {
          const favoritesRef = ref(database, `user/${studentId}/favorites`);
          const favoritesSnapshot = await get(favoritesRef);
          const favoritesData = favoritesSnapshot.exists() ? favoritesSnapshot.val() : {};

          // Fetch programs and schools data for ID lookups
          const programsRef = ref(database, 'programs');
          const programsSnapshot = await get(programsRef);
          const programs = programsSnapshot.exists() ? programsSnapshot.val() : {};
          setProgramsData(programs);

          const schoolsRef = ref(database, 'schools');
          const schoolsSnapshot = await get(schoolsRef);
          const schools = schoolsSnapshot.exists() ? schoolsSnapshot.val() : {};
          setSchoolsData(schools);

          // Create a map of school names for quick lookup
          const schoolNameMap = new Map<string, string>();
          for (const [schoolId, schoolData] of Object.entries(schools)) {
            if (schoolData && typeof schoolData === 'object' && (schoolData as any).SchoolName) {
              schoolNameMap.set((schoolData as any).SchoolName, schoolId);
            }
          }

          const schoolsSet = new Set<string>();
          const schoolsDetailedList: Array<{
            sortOrder: number;
            schoolName: string;
            schoolId?: string;
          }> = [];
          const programsSet = new Set<string>();
          const categoriesSet = new Set<string>();
          const programsDetailedList: Array<{
            sortOrder: number;
            programName: string;
            schoolName: string;
            programId: string;
            schoolId?: string;
          }> = [];

          // Process programs from favorites/programs node (legacy users)
          if (favoritesData.programs && typeof favoritesData.programs === 'object') {
            for (const [programKey, programData] of Object.entries(favoritesData.programs)) {
              if (programData && typeof programData === 'object') {
                const programId = (programData as any).FavoritedProgramID || programKey;
                let programName = (programData as any).FavoritedName;
                const sortOrder = (programData as any).FavoritedSortOrder || 0;
                const schoolName = (programData as any).FavoritedLocation || '';

                // Look up program by ID to get authoritative name and school
                let schoolId: string | undefined;
                if (programId && programs[programId]) {
                  const program = programs[programId];
                  programName = program.ProgramName || programName;
                  const programSchoolName = program.ProgramSchoolName || schoolName;
                  
                  // Find school ID by name
                  for (const [sid, sData] of Object.entries(schools)) {
                    if (sData && typeof sData === 'object' && (sData as any).SchoolName === programSchoolName) {
                      schoolId = sid;
                      break;
                    }
                  }
                  
                  if (program.ProgramCategory) {
                    categoriesSet.add(program.ProgramCategory);
                  }
                }
                
                if (programName && programName !== 'programs' && programName !== 'Program') {
                  programsSet.add(programName);
                  programsDetailedList.push({
                    sortOrder: sortOrder,
                    programName: programName,
                    schoolName: schoolName || '',
                    programId: programId,
                    schoolId: schoolId,
                  });
                }
              }
            }
          }

          // Process rest of favorites node (current users)
          for (const [itemName, itemData] of Object.entries(favoritesData)) {
            if (itemName === 'programs') continue; // Already processed

            if (itemData && typeof itemData === 'object') {
              const favoritedType = (itemData as any).FavoritedType;
              const isSchool = favoritedType === 'School' || favoritedType === 'school';
              const isProgram = favoritedType === 'Program' || favoritedType === 'program';
              const isKnownSchool = schoolNameMap.has(itemName);

              if (isSchool || (!isProgram && isKnownSchool)) {
                schoolsSet.add(itemName);
                
                // Get sort order and school ID for detailed list
                const schoolSortOrder = (itemData as any).FavoritedSortOrder || 0;
                let schoolId: string | undefined;
                
                // Find school ID by name
                for (const [sid, sData] of Object.entries(schools)) {
                  if (sData && typeof sData === 'object' && (sData as any).SchoolName === itemName) {
                    schoolId = sid;
                    break;
                  }
                }
                
                // Check if already added (avoid duplicates)
                const alreadyAdded = schoolsDetailedList.some(s => s.schoolName === itemName);
                if (!alreadyAdded) {
                  schoolsDetailedList.push({
                    sortOrder: schoolSortOrder,
                    schoolName: itemName,
                    schoolId: schoolId,
                  });
                }
              } else if (isProgram || (!isSchool && !isKnownSchool && itemData)) {
                const programIdFromData = (itemData as any).FavoritedProgramID || (itemData as any).ProgramID;
                const looksLikeProgramID = itemName.startsWith('-') || itemName.startsWith('FW_');
                const programId = programIdFromData || (looksLikeProgramID ? itemName : null);

                let programName = (itemData as any).FavoritedName || itemName;
                const sortOrder = (itemData as any).FavoritedSortOrder || 0;
                const schoolName = (itemData as any).FavoritedLocation || '';

                let schoolId: string | undefined;
                if (programId && programs[programId]) {
                  const program = programs[programId];
                  programName = program.ProgramName || programName;
                  const programSchoolName = program.ProgramSchoolName || schoolName;
                  
                  // Find school ID by name
                  for (const [sid, sData] of Object.entries(schools)) {
                    if (sData && typeof sData === 'object' && (sData as any).SchoolName === programSchoolName) {
                      schoolId = sid;
                      break;
                    }
                  }
                  
                  if (program.ProgramCategory) {
                    categoriesSet.add(program.ProgramCategory);
                  }
                }

                if (programName && programName !== 'programs' && programName !== 'Program') {
                  programsSet.add(programName);
                  
                  // Check if already added (avoid duplicates)
                  const alreadyAdded = programsDetailedList.some(p => p.programId === programId);
                  if (!alreadyAdded) {
                    programsDetailedList.push({
                      sortOrder: sortOrder,
                      programName: programName,
                      schoolName: schoolName || '',
                      programId: programId || '',
                      schoolId: schoolId,
                    });
                  }
                }
              }
            }
          }

          // Sort programs and schools by sortOrder
          programsDetailedList.sort((a, b) => a.sortOrder - b.sortOrder);
          schoolsDetailedList.sort((a, b) => a.sortOrder - b.sortOrder);

          setFavoriteSchools(Array.from(schoolsSet));
          setFavoriteSchoolsDetailed(schoolsDetailedList);
          setFavoritePrograms(Array.from(programsSet));
          setFavoriteProgramCategories(Array.from(categoriesSet));
          setFavoriteProgramsDetailed(programsDetailedList);

          // Fetch eligibility cache for all programs
          try {
            const eligibilityCacheRef = ref(database, `eligibilityCache/${studentId}`);
            const eligibilityCacheSnapshot = await get(eligibilityCacheRef);
            if (eligibilityCacheSnapshot.exists()) {
              setEligibilityCache(eligibilityCacheSnapshot.val());
            }
          } catch (eligibilityErr: any) {
            console.error('Error fetching eligibility cache:', eligibilityErr);
            // Don't fail if eligibility cache fails
          }
        } catch (favoritesErr: any) {
          console.error('Error fetching favorites:', favoritesErr);
          // Don't fail the whole page if favorites fail
        }
      } catch (err: any) {
        console.error('❌ Error fetching student data:', err);
        console.error('❌ Error code:', err.code, 'Error message:', err.message);
        setError(err.message || 'Failed to load student data');
      } finally {
        setLoading(false);
      }
    };

    fetchStudentData();
  }, [studentId, currentUser?.schoolID]);

  // Set up real-time listener for eligibility cache updates
  useEffect(() => {
    if (!studentId) return;

    // Listen directly to eligibility cache for real-time updates
    const eligibilityCacheRef = ref(database, `eligibilityCache/${studentId}`);
    
    const cacheListener = onValue(eligibilityCacheRef, (snapshot) => {
      if (snapshot.exists()) {
        // Eligibility cache updated - update state
        setEligibilityCache(snapshot.val());
      }
    }, (err) => {
      console.error('Error listening to eligibility cache:', err);
    });

    // Cleanup listener on unmount
    return () => {
      off(eligibilityCacheRef, 'value', cacheListener);
    };
  }, [studentId]);

  // Transform grades for display - must be defined before early returns
  const transformGradesToDisplayFormat = (gradesData: any, courseOpts: typeof courseOptions): Array<{name: string; courseName: string; grade: string; gradeType: 'actual' | 'projected'; year: string}> => {
    if (!gradesData || typeof gradesData !== 'object') return [];
    
    return Object.entries(gradesData).map(([courseCode, courseData]: [string, any]): {name: string; courseName: string; grade: string; gradeType: 'actual' | 'projected'; year: string} => {
      // Extract course information
      const gradeLevel = courseData.grade || courseData.Grade || '';
      const grades = courseData.grades || courseData.Grades || '';
      const gradeType = courseData.gradeType || courseData.GradeType || 'actual';
      const year = gradeLevel || courseData.year || courseData.Year || courseData.level || courseData.Level || '';
      
      // Look up course name from course options (skip courseData.name as it's the course code)
      let courseName = courseCode; // Default to course code
      const courseOption = courseOpts.find(opt => opt.code === courseCode);
      if (courseOption) {
        courseName = courseOption.description;
      } else {
        // Fallback: try courseData fields (but skip 'name' as it's the course code)
        courseName = courseData.Name || courseData.courseName || courseData.CourseName || courseCode;
      }

      return {
        name: courseCode,
        courseName: courseName,
        grade: grades ? grades.toString() : '',
        gradeType: gradeType as 'actual' | 'projected',
        year: year.toString(),
      };
    });
  };

  // Calculate display courses - must be before early returns (hooks rule)
  const grades = studentData?.grades || {};
  const displayCourses = useMemo(() => transformGradesToDisplayFormat(grades, courseOptions), [grades, courseOptions]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !studentData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={
          <Typography 
            variant="body2" 
            onClick={() => {
              // Preserve filters if coming from students list
              if (location.state?.fromStudents) {
                navigate('/students'); // URL params will be preserved automatically
              } else {
                navigate('/students', { state: { fromSidebar: true } });
              }
            }} 
            sx={{ cursor: 'pointer' }}
          >
            Back to Students
          </Typography>
        }>
          {error || 'Student data not available'}
        </Alert>
      </Box>
    );
  }

  // Convert student data to Student format
  const profileData = studentData.ProfileData || {};
  const profileExtra = studentData.profileExtra || {};
  const student: Student = {
    id: studentId || '',
    firstName: profileData['First Name'] || '',
    lastName: profileData['Last Name'] || '',
    grade: profileData['Class Year'] || '',
    school: profileData['High School'] || '',
    city: profileData['City'] || '',
    province: profileData['Province'] || '',
    interests: [],
    favoriteSchools: [],
    favoritePrograms: [],
    achievements: [],
    gpa: 0,
    email: profileData['Email'] || '',
    status: profileData['Status'] || 'Active',
    enrollmentDate: '',
    completedCourses: [],
    clubs: [],
    athletics: profileExtra.athleticsSportsProfile || {},
    activities: profileExtra.interests_ActivitiesProfile || {},
    workExperience: profileExtra.work_VolunteerExpProfile || {},
    registeredEventIds: [],
    athleticsSportsProfile: profileExtra.athleticsSportsProfile || {},
    athleticsAwardsProfile: profileExtra.athleticsAwardsProfile || {},
    academicAwardsHonoursProfile: profileExtra.academicAwardsHonoursProfile || {},
    academicExtenuatingCircumstancesProfile: profileExtra.academicExtenuatingCircumstancesProfile || {},
    workVolunteerExpProfile: profileExtra.work_VolunteerExpProfile || {},
    interestsActivitiesProfile: profileExtra.interests_ActivitiesProfile || {},
    eligibilityReport: {},
    grades: studentData.grades || {},
  };

  const profileImage = profileData.profileUrl || null;

  // Group courses by year/grade
  const grade12Courses = displayCourses.filter(c => {
    const yearStr = (c.year || '').toString().toLowerCase();
    return ['12', 'grade12', 'grade 12', '12th', 'gr12', 'g12'].includes(yearStr.replace(/\s+/g, ''));
  });
  const grade11Courses = displayCourses.filter(c => {
    const yearStr = (c.year || '').toString().toLowerCase();
    return ['11', 'grade11', 'grade 11', '11th', 'gr11', 'g11'].includes(yearStr.replace(/\s+/g, ''));
  });
  const grade10Courses = displayCourses.filter(c => {
    const yearStr = (c.year || '').toString().toLowerCase();
    return ['10', 'grade10', 'grade 10', '10th', 'gr10', 'g10'].includes(yearStr.replace(/\s+/g, ''));
  });

  const renderCoursesTable = (courses: typeof displayCourses) => {
    if (courses.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', p: 2 }}>
          No courses recorded
        </Typography>
      );
    }

    // Responsive widths for mobile landscape
    const courseCodeWidth = isMobileLandscape ? 80 : 120;
    const courseNameWidth = isMobileLandscape ? 200 : isTablet ? 350 : 500;
    const gradeWidth = isMobileLandscape ? 80 : 130;

    return (
      <TableContainer 
        component={Paper} 
        sx={{ 
          boxShadow: 'none', 
          maxWidth: isMobileLandscape ? '100%' : 880,
          overflowX: isMobileLandscape ? 'auto' : 'visible'
        }}
      >
        <Table size="small" sx={{ fontSize: 14, tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 'bold', width: courseCodeWidth }}>Course Code</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: courseNameWidth }}>Course Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: gradeWidth }}>Actual Grade</TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: gradeWidth }}>Projected Grade</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {courses.map((course, idx) => (
              <TableRow key={idx} sx={{ backgroundColor: idx % 2 === 0 ? 'white' : 'grey.50' }}>
                <TableCell sx={{ width: courseCodeWidth, whiteSpace: 'nowrap' }}>{course.name}</TableCell>
                <TableCell sx={{ 
                  width: courseNameWidth, 
                  wordWrap: 'break-word', 
                  overflowWrap: 'break-word',
                  whiteSpace: 'normal',
                  pr: 2,
                  fontSize: isMobileLandscape ? '0.75rem' : '0.875rem'
                }}>
                  {course.courseName || ''}
                </TableCell>
                <TableCell sx={{ textAlign: 'center', width: gradeWidth, whiteSpace: 'nowrap' }}>
                  {course.gradeType === 'actual' && course.grade ? `${course.grade}%` : ''}
                </TableCell>
                <TableCell sx={{ textAlign: 'center', width: gradeWidth, whiteSpace: 'nowrap' }}>
                  {course.gradeType === 'projected' && course.grade ? `${course.grade}%` : ''}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Handler functions for school action chips
  const handleSchoolWebsiteClick = async (event: React.MouseEvent, schoolId: string | undefined, schoolData: any) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (schoolData?.website && currentUser?.uid && schoolId) {
      // Log the external school website referral activity
      try {
        await activityLogService.logExternalSchoolWebsiteReferral(currentUser.uid, schoolId);
      } catch (error) {
        console.error('Error logging external school website referral activity:', error);
        // Don't block the flow if logging fails
      }
      
      // Format the URL properly
      const formattedUrl = formatWebsiteUrl(schoolData.website);
      
      // Try different approaches for mobile vs desktop
      try {
        if (isMobile) {
          // For mobile, try using location.href first, then fallback to window.open
          window.location.href = formattedUrl;
        } else {
          // For desktop, use window.open
          window.open(formattedUrl, '_blank');
        }
      } catch (error) {
        console.error('Error opening external link:', error);
        // Fallback: try window.open for all devices
        window.open(formattedUrl, '_blank');
      }
    }
  };

  const handleSchoolCatalogClick = async (event: React.MouseEvent, schoolId: string | undefined, schoolData: any) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (schoolData?.SchoolCatalog && currentUser?.uid && schoolId) {
      // Log the external school catalog referral activity
      try {
        await activityLogService.logExternalSchoolWebsiteReferral(currentUser.uid, schoolId);
      } catch (error) {
        console.error('Error logging external school catalog referral activity:', error);
        // Don't block the flow if logging fails
      }
      
      // Format the URL properly
      const formattedUrl = formatWebsiteUrl(schoolData.SchoolCatalog);
      
      // Try different approaches for mobile vs desktop
      try {
        if (isMobile) {
          // For mobile, try using location.href first, then fallback to window.open
          window.location.href = formattedUrl;
        } else {
          // For desktop, use window.open
          window.open(formattedUrl, '_blank');
        }
      } catch (error) {
        console.error('Error opening external link:', error);
        // Fallback: try window.open for all devices
        window.open(formattedUrl, '_blank');
      }
    }
  };

  const handleVirtualTourClick = async (event: React.MouseEvent, schoolId: string | undefined, schoolData: any) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (schoolData?.SchoolVirtualTourURL && currentUser?.uid && schoolId) {
      // Log the external virtual tour referral activity
      try {
        await activityLogService.logExternalSchoolWebsiteReferral(currentUser.uid, schoolId);
      } catch (error) {
        console.error('Error logging external virtual tour referral activity:', error);
        // Don't block the flow if logging fails
      }
      
      // Format the URL properly
      const formattedUrl = formatWebsiteUrl(schoolData.SchoolVirtualTourURL);
      
      // Try different approaches for mobile vs desktop
      try {
        if (isMobile) {
          // For mobile, try using location.href first, then fallback to window.open
          window.location.href = formattedUrl;
        } else {
          // For desktop, use window.open
          window.open(formattedUrl, '_blank');
        }
      } catch (error) {
        console.error('Error opening external link:', error);
        // Fallback: try window.open for all devices
        window.open(formattedUrl, '_blank');
      }
    }
  };

  const handleCampusTourClick = async (event: React.MouseEvent, schoolId: string | undefined, schoolData: any) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (schoolData?.SchoolCampusTourURL && currentUser?.uid && schoolId) {
      // Log the external campus tour referral activity
      try {
        await activityLogService.logExternalSchoolWebsiteReferral(currentUser.uid, schoolId);
      } catch (error) {
        console.error('Error logging external campus tour referral activity:', error);
        // Don't block the flow if logging fails
      }
      
      // Format the URL properly
      const formattedUrl = formatWebsiteUrl(schoolData.SchoolCampusTourURL);
      
      // Try different approaches for mobile vs desktop
      try {
        if (isMobile) {
          // For mobile, try using location.href first, then fallback to window.open
          window.location.href = formattedUrl;
        } else {
          // For desktop, use window.open
          window.open(formattedUrl, '_blank');
        }
      } catch (error) {
        console.error('Error opening external link:', error);
        // Fallback: try window.open for all devices
        window.open(formattedUrl, '_blank');
      }
    }
  };

  // Check if we came from a program profile
  const fromProgram = location.state?.fromProgram === true;
  const programId = location.state?.programId;
  const fromGuidance = location.state?.fromGuidance === true;

  return (
    <Box sx={{ p: 2 }}>
      {/* Back button if coming from program profile */}
      {fromProgram && programId && (
        <Button 
          variant="outlined" 
          sx={{ mb: 2 }} 
          onClick={() => {
            // Preserve fromGuidance parameter if we came from guidance dashboard
            const url = fromGuidance 
              ? `/programs/${programId}?fromGuidance=1`
              : `/programs/${programId}`;
            navigate(url);
          }}
        >
          &larr; Back to Program
        </Button>
      )}
      
      <Paper sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Avatar
            src={(profileImage || currentUser?.userIcon) ?? undefined}
            alt={`${student.firstName} ${student.lastName}`}
            sx={{ 
              width: 80, 
              height: 80, 
              mr: 2,
              ...(!profileImage && {
                '& .MuiAvatar-img': {
                  objectFit: 'contain',
                },
              }),
            }}
          />
          <Box>
            <Typography variant="h4">
              {student.firstName} {student.lastName}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {student.school || 'No school specified'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Class of {student.grade || 'N/A'} • {student.email}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Tabs */}
        <Tabs value={selectedTab} onChange={(_, newValue) => setSelectedTab(newValue)} sx={{ mb: 3 }}>
          <Tab label="Favourites" />
          <Tab label="Academics" />
          <Tab label="Athletics" />
          <Tab label="Work & Volunteering" />
          <Tab label="Clubs & Activities" />
        </Tabs>

        {/* Favourites Tab */}
        {selectedTab === 0 && (
          <Box>
            {/* Schools Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Favourite Schools</Typography>
              {favoriteSchoolsDetailed.length > 0 ? (
                <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'grey.100' }}>
                        <TableCell sx={{ fontWeight: 'bold', width: 80 }}></TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>School Name</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {favoriteSchoolsDetailed.map((school, idx) => {
                        // Get school logo from Schools Context
                        const schoolData = schools.find(s => 
                          s.name === school.schoolName || 
                          (school.schoolId && s.id === school.schoolId)
                        );
                        const schoolLogo = schoolData?.feedIcon || schoolData?.resultsImage || null;
                        
                        // Find school ID if not already available and get full school data
                        let schoolIdToUse = school.schoolId;
                        let fullSchoolData: any = null;
                        if (!schoolIdToUse) {
                          for (const [sid, sData] of Object.entries(schoolsData)) {
                            if (sData && typeof sData === 'object' && (sData as any).SchoolName === school.schoolName) {
                              schoolIdToUse = sid;
                              fullSchoolData = sData;
                              break;
                            }
                          }
                        } else {
                          // Get full school data from schoolsData using schoolId
                          fullSchoolData = schoolsData[schoolIdToUse] || null;
                          // If not found in schoolsData, try to find it by name as fallback
                          if (!fullSchoolData) {
                            for (const [sid, sData] of Object.entries(schoolsData)) {
                              if (sData && typeof sData === 'object' && (sData as any).SchoolName === school.schoolName) {
                                fullSchoolData = sData;
                                break;
                              }
                            }
                          }
                        }
                        
                        return (
                          <TableRow 
                            key={idx}
                            sx={{ 
                              backgroundColor: idx % 2 === 0 ? 'white' : 'grey.50',
                              '&:hover': {
                                backgroundColor: 'action.hover',
                              },
                            }}
                          >
                            <TableCell sx={{ width: 80, verticalAlign: 'top', textAlign: 'center' }}>{idx + 1}</TableCell>
                            <TableCell
                              sx={{
                                cursor: 'pointer',
                              }}
                              onClick={() => {
                                if (schoolIdToUse) {
                                  navigate(`/schools/${schoolIdToUse}?fromStudent=${studentId}`);
                                } else {
                                  console.error('School ID not found for:', school.schoolName);
                                }
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <Box sx={{ pt: 0.5 }}>
                                  {schoolLogo ? (
                                    <Avatar
                                      src={schoolLogo}
                                      alt={school.schoolName}
                                      sx={{
                                        width: 24,
                                        height: 24,
                                        border: '0.5px solid black',
                                        '& .MuiAvatar-img': {
                                          objectFit: 'contain',
                                        },
                                      }}
                                    />
                                  ) : (
                                    <Avatar
                                      sx={{
                                        width: 24,
                                        height: 24,
                                        bgcolor: 'grey.200',
                                        color: 'grey.600',
                                        fontSize: '0.75rem',
                                      }}
                                    >
                                      {school.schoolName ? school.schoolName[0] : ''}
                                    </Avatar>
                                  )}
                                </Box>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontSize: '1rem',
                                  }}
                                >
                                  {school.schoolName}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top' }}>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {fullSchoolData?.website && (
                                  <Chip 
                                    label="Website" 
                                    size="small" 
                                    clickable 
                                    onClick={(e) => handleSchoolWebsiteClick(e, schoolIdToUse, fullSchoolData)}
                                  />
                                )}
                                {fullSchoolData?.SchoolCatalog && (
                                  <Chip 
                                    label="Viewbook" 
                                    size="small" 
                                    clickable 
                                    onClick={(e) => handleSchoolCatalogClick(e, schoolIdToUse, fullSchoolData)}
                                  />
                                )}
                                {fullSchoolData?.SchoolVirtualTourURL && (
                                  <Chip 
                                    label="Virtual Tour" 
                                    size="small" 
                                    clickable 
                                    onClick={(e) => handleVirtualTourClick(e, schoolIdToUse, fullSchoolData)}
                                  />
                                )}
                                {fullSchoolData?.SchoolCampusTourURL && (
                                  <Chip 
                                    label="Book Campus Tour" 
                                    size="small" 
                                    clickable 
                                    onClick={(e) => handleCampusTourClick(e, schoolIdToUse, fullSchoolData)}
                                  />
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No favorite schools
                </Typography>
              )}
            </Box>

            {/* Programs Section - Table Format */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>Favourite Programs</Typography>
              {favoriteProgramsDetailed.length > 0 ? (
                <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'grey.100' }}>
                        <TableCell sx={{ fontWeight: 'bold', width: 80 }}></TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Program Name</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', width: 400 }}>School Name</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {favoriteProgramsDetailed.map((program, idx) => {
                        // Get school logo from Schools Context
                        const school = schools.find(s => 
                          s.name === program.schoolName || 
                          (program.schoolId && s.id === program.schoolId)
                        );
                        const schoolLogo = school?.feedIcon || school?.resultsImage || null;
                        
                        // Get eligibility data for this program
                        const programEligibility = eligibilityCache[program.programId] || null;
                        
                        // Get student grade for eligibility icon
                        const studentGrade = student.grade || '10';
                        
                        return (
                          <TableRow 
                            key={idx} 
                            sx={{ 
                              backgroundColor: idx % 2 === 0 ? 'white' : 'grey.50',
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: 'action.hover',
                              },
                            }}
                            onClick={() => {
                              if (program.programId) {
                                navigate(`/programs/${program.programId}?fromStudent=${studentId}`);
                              }
                            }}
                          >
                            <TableCell sx={{ width: 80, verticalAlign: 'top', textAlign: 'center' }}>{idx + 1}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <Box sx={{ pt: 0.5 }}>
                                  <EligibilityIcon
                                    eligibility={programEligibility as EligibilityData | null}
                                    userGrade={studentGrade}
                                    size={20}
                                  />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body1" sx={{ mb: 0.5, fontSize: '1rem' }}>
                                    {program.programName}
                                  </Typography>
                                  {programEligibility?.alertMessage && (
                                    <Box sx={{ mb: 0.5 }}>
                                      <Typography variant="body1" color="text.secondary" component="span" sx={{ fontSize: '1rem' }}>
                                        <span style={{ fontStyle: 'italic', textDecoration: 'underline' }}>Eligibility Notes</span>:{' '}
                                      </Typography>
                                      <Typography variant="body1" color="text.secondary" component="span" sx={{ fontSize: '1rem' }}>
                                        {programEligibility.alertMessage}
                                      </Typography>
                                    </Box>
                                  )}
                                  {programEligibility?.nextYearSuggestion && programEligibility.nextYearSuggestion !== "" && (
                                    <Box>
                                      <Typography variant="body1" color="text.secondary" component="span" sx={{ fontSize: '1rem' }}>
                                        <span style={{ fontStyle: 'italic', textDecoration: 'underline' }}>Suggestions for Next Year</span>:{' '}
                                      </Typography>
                                      <Typography variant="body1" color="text.secondary" component="span" sx={{ fontSize: '1rem' }}>
                                        {programEligibility.nextYearSuggestion}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ width: 400, verticalAlign: 'top' }}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <Box sx={{ pt: 0.5 }}>
                                  {schoolLogo ? (
                                    <Avatar
                                      src={schoolLogo}
                                      alt={program.schoolName}
                                      sx={{
                                        width: 24,
                                        height: 24,
                                        border: '0.5px solid black',
                                        '& .MuiAvatar-img': {
                                          objectFit: 'contain',
                                        },
                                      }}
                                    />
                                  ) : (
                                    <Avatar
                                      sx={{
                                        width: 24,
                                        height: 24,
                                        bgcolor: 'grey.200',
                                        color: 'grey.600',
                                        fontSize: '0.75rem',
                                      }}
                                    >
                                      {program.schoolName ? program.schoolName[0] : ''}
                                    </Avatar>
                                  )}
                                </Box>
                                <Typography variant="body1" sx={{ fontSize: '1rem' }}>{program.schoolName || 'N/A'}</Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No favourite programs
                </Typography>
              )}
              <Box sx={{ mt: 2 }}>
                <Link
                  component="button"
                  onClick={() => setEligibilityIndicatorsModalOpen(true)}
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    font: 'inherit',
                    fontSize: '0.875rem',
                    '&:hover': {
                      color: 'primary.dark',
                    },
                  }}
                >
                  Eligibility Indicator definitions
                </Link>
              </Box>
            </Box>
          </Box>
        )}

        {/* Academics Tab */}
        {selectedTab === 1 && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h5">Courses & Grades</Typography>
              {grade12Courses.length > 0 || grade11Courses.length > 0 || grade10Courses.length > 0 ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCopyCoursesToGuidanceUser}
                  disabled={copyingCourses || !authUser?.uid}
                  sx={{ ml: 2 }}
                >
                  {copyingCourses ? 'Copying...' : 'Copy Courses & Grades'}
                </Button>
              ) : null}
            </Box>
            
            {grade12Courses.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Grade 12</Typography>
                {renderCoursesTable(grade12Courses)}
              </Box>
            )}

            {grade11Courses.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Grade 11</Typography>
                {renderCoursesTable(grade11Courses)}
              </Box>
            )}

            {grade10Courses.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Grade 10</Typography>
                {renderCoursesTable(grade10Courses)}
              </Box>
            )}

            {grade12Courses.length === 0 && grade11Courses.length === 0 && grade10Courses.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No course data available
              </Typography>
            )}

            {/* Awards & Honours */}
            {student.academicAwardsHonoursProfile && Object.keys(student.academicAwardsHonoursProfile).length > 0 && (
              <Box sx={{ mb: 4, mt: 4 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Awards & Honours</Typography>
                <Grid container spacing={2}>
                  {Object.entries(student.academicAwardsHonoursProfile).map(([id, award]: [string, any]) => (
                    <Grid item xs={12} sm={6} key={id}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {award.title || award.Title || 'Award'}
                        </Typography>
                        {(award.grade || award.Grade) && (
                          <Typography variant="body2" color="text.secondary">
                            Grade: {award.grade || award.Grade}
                          </Typography>
                        )}
                        {(award.yearReceived || award.YearReceived) && (
                          <Typography variant="body2" color="text.secondary">
                            Year: {award.yearReceived || award.YearReceived}
                          </Typography>
                        )}
                        {(award.description || award.Description) && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {award.description || award.Description}
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Extenuating Circumstances */}
            {student.academicExtenuatingCircumstancesProfile && Object.keys(student.academicExtenuatingCircumstancesProfile).length > 0 && (
              <Box sx={{ mb: 4, mt: 4 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Extenuating Circumstances</Typography>
                <Grid container spacing={2}>
                  {Object.entries(student.academicExtenuatingCircumstancesProfile).map(([id, circumstance]: [string, any]) => (
                    <Grid item xs={12} key={id}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="body2">
                          {circumstance.description || circumstance.Description || 'No description provided'}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Box>
        )}

        {/* Athletics Tab */}
        {selectedTab === 2 && (
          <Box>
            <Typography variant="h5" sx={{ mb: 2 }}>Athletics</Typography>
            {student.athleticsSportsProfile && Object.keys(student.athleticsSportsProfile).length > 0 ? (
              <Grid container spacing={2}>
                {Object.entries(student.athleticsSportsProfile).map(([id, sport]: [string, any]) => (
                  <Grid item xs={12} sm={6} key={id}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {id || 'Sport'}
                      </Typography>
                      {sport.position || sport.Position ? (
                        <Typography variant="body2" color="text.secondary">
                          Position: {sport.position || sport.Position}
                        </Typography>
                      ) : null}
                      {sport.level || sport.Level ? (
                        <Typography variant="body2" color="text.secondary">
                          Level: {sport.level || sport.Level}
                        </Typography>
                      ) : null}
                      {(sport.startGrade || sport.StartGrade) && (sport.endGrade || sport.EndGrade) ? (
                        <Typography variant="body2" color="text.secondary">
                          Grades: {sport.startGrade || sport.StartGrade} - {sport.endGrade || sport.EndGrade}
                        </Typography>
                      ) : null}
                      {(sport.description || sport.Description) && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {sport.description || sport.Description}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No athletics recorded
              </Typography>
            )}
          </Box>
        )}

        {/* Work & Volunteering Tab */}
        {selectedTab === 3 && (
          <Box>
            <Typography variant="h5" sx={{ mb: 2 }}>Work & Volunteering</Typography>
            {student.workVolunteerExpProfile && Object.keys(student.workVolunteerExpProfile).length > 0 ? (
              <Grid container spacing={2}>
                {Object.entries(student.workVolunteerExpProfile).map(([id, work]: [string, any]) => (
                  <Grid item xs={12} sm={6} key={id}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {work.position || work.Position || 'Position'}
                      </Typography>
                      {work.employer || work.Employer ? (
                        <Typography variant="body2" color="text.secondary">
                          {work.employer || work.Employer}
                        </Typography>
                      ) : null}
                      {(work.startGrade || work.StartGrade) && (work.endGrade || work.EndGrade) ? (
                        <Typography variant="body2" color="text.secondary">
                          Grades: {work.startGrade || work.StartGrade} - {work.endGrade || work.EndGrade}
                        </Typography>
                      ) : null}
                      {(work.description || work.Description) && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {work.description || work.Description}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No work or volunteering experience recorded
              </Typography>
            )}
          </Box>
        )}

        {/* Clubs & Activities Tab */}
        {selectedTab === 4 && (
          <Box>
            <Typography variant="h5" sx={{ mb: 2 }}>Clubs & Activities</Typography>
            {student.interestsActivitiesProfile && Object.keys(student.interestsActivitiesProfile).length > 0 ? (
              <Grid container spacing={2}>
                {Object.entries(student.interestsActivitiesProfile).map(([id, activity]: [string, any]) => (
                  <Grid item xs={12} sm={6} key={id}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {activity.interest || activity.Interest || 'Activity'}
                      </Typography>
                      {(activity.startGrade || activity.StartGrade) && (activity.endGrade || activity.EndGrade) ? (
                        <Typography variant="body2" color="text.secondary">
                          Grades: {activity.startGrade || activity.StartGrade} - {activity.endGrade || activity.EndGrade}
                        </Typography>
                      ) : null}
                      {(activity.description || activity.Description) && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {activity.description || activity.Description}
                        </Typography>
                      )}
                      {(activity.link || activity.Link) && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <a href={activity.link || activity.Link} target="_blank" rel="noopener noreferrer">
                            {activity.link || activity.Link}
                          </a>
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No clubs or activities recorded
              </Typography>
            )}
          </Box>
        )}
      </Paper>

      {/* Custom Eligibility Indicators Modal */}
      <CustomEligibilityIndicatorsModal
        open={eligibilityIndicatorsModalOpen}
        onClose={() => setEligibilityIndicatorsModalOpen(false)}
      />
    </Box>
  );
};

export default StudentProfileForGuidance;

